import React from 'react';

import castArray from 'lodash/castArray';

import { useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { SelectionChipGroup } from '@lumapps/lumx-inputs/components/SelectionChipGroup';
import { getWithSelector } from '@lumapps/lumx/core/utils/selectors';
import { type TextFieldProps } from '@lumapps/lumx/react';
import { InfiniteScroll } from '@lumapps/utils/hooks/useInfiniteScroll';
import { mergeRefs } from '@lumapps/utils/react/mergeRefs';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import type {
    BaseSelectProps,
    ComboboxProps,
    MultipleSelection,
    OnComboboxInputChange,
    OnComboboxSelect,
    SingleSelection,
} from '../../types';
import { renderSelectOptions } from '../../utils/renderSelectOptions';
import { Combobox } from '../Combobox';
import type { ComboboxInputProps } from '../ComboboxInput';

import './index.scss';

type InheritComboboxProps = Pick<
    ComboboxProps,
    'onOpen' | 'status' | 'autoFilter' | 'showEmptyState' | 'showErrorState' | 'openOnFocus' | 'translations'
>;
type InheritTextFieldProps = Pick<
    ComboboxInputProps,
    | 'aria-disabled'
    | 'theme'
    | 'className'
    | 'icon'
    | 'label'
    | 'helper'
    | 'placeholder'
    | 'error'
    | 'hasError'
    | 'isDisabled'
    | 'isRequired'
    | 'onBlur'
    | 'onFocus'
    | 'id'
    | 'name'
    | 'inputRef'
    | 'isValid'
    | 'readOnly'
    | 'maxLength'
    | 'hideToggle'
    | 'onKeyDown'
    | 'ariaLabel'
    | 'onClear'
> & {
    autoComplete?: string;
    labelProps?: Partial<Omit<TextFieldProps['labelProps'], 'children' | 'htmlFor'>>;
};
type ForwardedProps = InheritComboboxProps & InheritTextFieldProps;

type BaseSelectTextFieldProps = {
    /**
     * Callback on input search change
     */
    onSearch?(searchText: string): void;
    /** whether the search is enabled or not. */
    isSearchEnabled?: boolean;
    /** initial search input value */
    searchInputValue?: string;
    /**
     * Whether the clear button should be displayed (only in single selection mode)
     */
    hasClearButton?: ComboboxInputProps['hasClearButton'];
};

export type SingleSelectTextFieldProps<O = any> = BaseSelectProps<O> &
    BaseSelectTextFieldProps &
    SingleSelection<O> &
    ForwardedProps;
export type MultipleSelectTextFieldProps<O = any> = BaseSelectProps<O> &
    BaseSelectTextFieldProps &
    MultipleSelection<O> &
    ForwardedProps;

/**
 * SelectTextField props
 */
export type SelectTextFieldProps<O = any> = SingleSelectTextFieldProps<O> | MultipleSelectTextFieldProps<O>;

const CLASSNAME = 'lumx-select-text-field';

/**
 * Select text field
 *
 * An accessible text field with a list box showing a list of options,
 * allowing the user to search and pick an item from this list.
 * It follows the combobox pattern.
 *
 * @family Combobox
 */
export const SelectTextField = <O,>(props: SelectTextFieldProps<O>) => {
    const {
        options,
        getOptionId,
        getOptionName = getOptionId,
        getOptionDescription,
        getSectionId,
        renderSectionTitle,
        renderOption,
        value,
        onChange,
        onOpen,
        onBlur,
        onSearch,
        onLoadMore,
        status,
        autoFilter,
        selectionType,
        hasClearButton = true,
        inputRef: forwardedInputRef,
        translations,
        isSearchEnabled = true,
        name,
        renderChip,
        listProps,
        scope = CLASSNAME,
        showErrorState,
        showEmptyState,
        openOnFocus,
        theme,
        beforeOptions,
        readOnly,
        searchInputValue: forwardedSearchInputValue,
        isDisabled,
        id,
        ...forwardedProps
    } = props;

    const [listElement, setListElement] = React.useState<HTMLElement | null>(null);
    const isValueDefined = value !== undefined;
    const { block, element } = useClassnames(CLASSNAME);
    const { get } = useDataAttributes(scope);
    const isClearable = selectionType === 'single' && hasClearButton;

    const handleLoadMore = React.useCallback(() => {
        if (
            onLoadMore &&
            status !== BaseLoadingStatus.error &&
            status !== BaseLoadingStatus.empty &&
            status !== BaseLoadingStatus.loadingMore &&
            status !== BaseLoadingStatus.loading
        ) {
            onLoadMore();
        }
    }, [onLoadMore, status]);

    // Use option id
    const selectedIds = isValueDefined
        ? castArray(value)
              .map((v) => getWithSelector(getOptionId, v))
              .filter((v) => v !== undefined && v !== null)
        : [];
    const defaultInputValue =
        (isValueDefined && selectionType === 'single' && getWithSelector(getOptionName, value)) || '';
    const [searchInputValue, setSearchInputValue] = React.useState<string | undefined>(forwardedSearchInputValue);

    React.useEffect(() => {
        if (
            forwardedSearchInputValue !== undefined &&
            searchInputValue !== undefined &&
            forwardedSearchInputValue !== searchInputValue
        ) {
            setSearchInputValue(forwardedSearchInputValue);
        }
    }, [forwardedSearchInputValue, searchInputValue]);

    const onInputChange = React.useCallback<OnComboboxInputChange>(
        (searchText, _, event) => {
            if (isSearchEnabled) {
                setSearchInputValue(searchText);
                if (event) {
                    // Only when actually typing (not when selecting a value)
                    onSearch?.(searchText);
                }
                // Input cleared => clear selection
                if (isClearable && !searchText) {
                    onChange?.(undefined);
                }
            }
        },
        [isSearchEnabled, isClearable, onSearch, onChange],
    );

    // Map option id selection to option object selection
    const onSelect = React.useCallback<OnComboboxSelect>(
        (selectedOption) => {
            const selectedOptionId = selectedOption?.id;
            const newOption = selectedOptionId
                ? options?.find((option) => {
                      const optionId = getWithSelector(getOptionId, option);
                      return optionId === selectedOptionId;
                  })
                : undefined;

            if (selectionType === 'single') {
                onChange?.(newOption);
            } else if (selectionType === 'multiple' && newOption) {
                const newValue = isValueDefined ? [...value] : [];

                // Check if new option is already in value
                const existingIndex = newValue.findIndex((v) => getWithSelector(getOptionId, v) === selectedOptionId);

                // Toggle selected option in new value
                if (existingIndex === -1) {
                    newValue.push(newOption);
                } else {
                    newValue.splice(existingIndex, 1);
                }
                onChange?.(newValue);
            }
            // Reset search input value (defaults to selected option value)
            setSearchInputValue(undefined);
            // Reset the search to get the full list on the next opening after a selection
            // We don't refetch the list if the search was already empty as we already have the full list loaded.
            if (searchInputValue !== '') {
                onSearch?.('');
            }
        },
        [options, selectionType, searchInputValue, getOptionId, onChange, isValueDefined, value, onSearch],
    );

    const handleBlur = React.useCallback(
        (event: React.FocusEvent) => {
            onBlur?.(event);
            // Reset search input value (defaults to selected option value)
            setSearchInputValue(undefined);
        },
        [onBlur],
    );

    // Focus last chip on backspace pressed at the first position of the input
    const inputRef = React.useRef<HTMLInputElement>(null);

    return (
        <Combobox
            id={id}
            selectedIds={selectedIds}
            showEmptyState={showEmptyState}
            showErrorState={showErrorState}
            onSelect={onSelect}
            onOpen={onOpen}
            onInputChange={onInputChange}
            inputValue={searchInputValue !== undefined ? searchInputValue : defaultInputValue}
            openOnClick={!readOnly}
            openOnFocus={openOnFocus}
            status={status}
            autoFilter={autoFilter}
            selectionType={selectionType}
            translations={translations}
        >
            <Combobox.Input
                {...get({ element: 'input' })}
                {...forwardedProps}
                id={id}
                theme={theme}
                className={block([forwardedProps.className])}
                name={name}
                inputRef={mergeRefs([inputRef, forwardedInputRef])}
                onBlur={handleBlur}
                // Activate clear button on single select & only when we have a value
                hasClearButton={isClearable && isValueDefined}
                clearButtonProps={get({ element: 'clear' })}
                // Display multi selection
                chips={
                    selectionType === 'multiple' ? (
                        <SelectionChipGroup
                            theme={theme}
                            scope={scope}
                            renderChip={renderChip}
                            value={value}
                            getOptionName={getOptionName}
                            getOptionId={getOptionId}
                            onChange={onChange}
                            inputRef={inputRef}
                            inputLabel={forwardedProps.label}
                            isDisabled={isDisabled}
                        />
                    ) : null
                }
                readOnly={readOnly}
                isDisabled={isDisabled}
            />
            <Combobox.List
                aria-multiselectable={selectionType === 'multiple'}
                listRef={setListElement}
                popoverProps={{
                    fitToAnchorWidth: 'width',
                    ...listProps?.popoverProps,
                }}
                {...get({ element: 'list' })}
                {...listProps}
                className={element('list', [listProps?.className])}
            >
                {beforeOptions}
                {renderSelectOptions({
                    options,
                    getSectionId,
                    renderSectionTitle,
                    renderOption,
                    getOptionId,
                    getOptionName,
                    getOptionDescription,
                })}

                {onLoadMore ? (
                    <InfiniteScroll callback={handleLoadMore} options={{ root: listElement, rootMargin: '100px' }} />
                ) : null}
            </Combobox.List>
        </Combobox>
    );
};
